/* istanbul ignore file */
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { listUsers, type User } from "@app/services/adminUsersApi";
import {
  sendBroadcast,
  type BroadcastChannel,
  type BroadcastResponse,
  type BroadcastTemplate,
} from "@app/services/adminBroadcastApi";

const TEMPLATE_OPTIONS: { value: BroadcastTemplate; label: string }[] = [
  { value: "today_profit", label: "Daily Profit (per user)" },
  { value: "today_report", label: "Daily Report (global)" },
  { value: "week_profit", label: "Weekly Profit (per user)" },
  { value: "week_report", label: "Weekly Report (global)" },
  { value: "month_profit", label: "Monthly Profit (per user)" },
  { value: "month_report", label: "Monthly Report (global)" },
  { value: "subscription_reminder", label: "Subscription Reminder" },
  { value: "custom", label: "Custom Message" },
];

interface AdminBroadcastCardProps {
  isAdmin: boolean;
}

export function AdminBroadcastCard({
  isAdmin,
}: AdminBroadcastCardProps): JSX.Element | null {
  const [template, setTemplate] = useState<BroadcastTemplate>("today_profit");
  const [allUsers, setAllUsers] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [channels, setChannels] = useState<BroadcastChannel[]>([
    "telegram",
    "email",
  ]);
  const [customTitle, setCustomTitle] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<BroadcastResponse | null>(null);

  const { data: users = [], isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ["admin", "users"],
    queryFn: () => listUsers(0, 500),
    enabled: isAdmin,
  });

  const recipients = useMemo(
    () => users.filter((u) => u.status === "active"),
    [users],
  );

  const filteredRecipients = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return recipients;
    }
    return recipients.filter((user) =>
      `${user.name ?? ""} ${user.email ?? ""}`.toLowerCase().includes(query),
    );
  }, [recipients, searchQuery]);

  const allFilteredSelected =
    filteredRecipients.length > 0 &&
    filteredRecipients.every((user) => selectedIds.includes(user.id));

  const broadcastMutation = useMutation({
    mutationFn: sendBroadcast,
    onMutate: () => {
      setError(null);
      setLastResult(null);
    },
    onSuccess: (result) => setLastResult(result),
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Failed to send broadcast.");
    },
  });

  if (!isAdmin) {
    return null;
  }

  const toggleChannel = (channel: BroadcastChannel) => {
    setChannels((prev) =>
      prev.includes(channel)
        ? prev.filter((c) => c !== channel)
        : [...prev, channel],
    );
  };

  const toggleUser = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAllFiltered = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) =>
        prev.filter(
          (id) => !filteredRecipients.some((user) => user.id === id),
        ),
      );
    } else {
      const idsToAdd = filteredRecipients
        .map((user) => user.id)
        .filter((id) => !selectedIds.includes(id));
      setSelectedIds((prev) => [...prev, ...idsToAdd]);
    }
  };

  const handleSend = () => {
    if (!allUsers && selectedIds.length === 0) {
      setError("Select at least one recipient or enable 'All users'.");
      return;
    }
    if (channels.length === 0) {
      setError("Select at least one delivery channel.");
      return;
    }
    if (template === "custom" && !customMessage.trim()) {
      setError("Enter a message body for the custom template.");
      return;
    }

    broadcastMutation.mutate({
      recipient_ids: allUsers ? [] : selectedIds,
      all_users: allUsers,
      template,
      title: template === "custom" ? customTitle.trim() || null : null,
      message: template === "custom" ? customMessage.trim() : null,
      delivery_channels: channels,
    });
  };

  return (
    <Card
      className="overview-card integrations-card"
      title="Broadcast to Subscribers"
      subtitle="Send a message to one user, several users, or everyone. Delivery goes through Telegram and/or email, with a copy saved to each user's in-app notifications."
    >
      <div className="section-legend">
        <span className="section-legend__dot" />
        <div>
          Choose a template, pick the recipients, then send. Messages are
          delivered per-user so one failure never blocks the rest.
        </div>
      </div>

      <form
        className="integrations-form"
        onSubmit={(event) => {
          event.preventDefault();
          handleSend();
        }}
      >
        <label>
          Template
          <select
            value={template}
            onChange={(event) =>
              setTemplate(event.target.value as BroadcastTemplate)
            }
          >
            {TEMPLATE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        {template === "custom" && (
          <>
            <label>
              Title
              <input
                type="text"
                value={customTitle}
                onChange={(event) => setCustomTitle(event.target.value)}
                placeholder="Announcement title"
              />
            </label>
            <label>
              Message
              <textarea
                value={customMessage}
                onChange={(event) => setCustomMessage(event.target.value)}
                placeholder="Write the message to send..."
                rows={4}
              />
            </label>
          </>
        )}

        <div className="integrations-checkbox">
          <input
            type="checkbox"
            checked={allUsers}
            onChange={(event) => setAllUsers(event.target.checked)}
          />
          Send to all users
        </div>

        {!allUsers && (
          <div className="connections-list">
            <div className="connections-list__header">
              <label
                className="connections-item"
                style={{ cursor: "pointer" }}
              >
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleAllFiltered}
                  disabled={filteredRecipients.length === 0}
                />
                <span className="connections-item__info">
                  <span className="connections-item__name">
                    Select all visible
                  </span>
                  <div className="connections-item__meta">
                    {selectedIds.length} selected
                  </div>
                </span>
              </label>
              <input
                type="text"
                className="connections-search"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>

            <div style={{ maxHeight: "220px", overflowY: "auto" }}>
              {usersLoading ? (
                <div className="integrations-empty">Loading users...</div>
              ) : filteredRecipients.length === 0 ? (
                <div className="integrations-empty">
                  {recipients.length === 0
                    ? "No active users available."
                    : "No users match your search."}
                </div>
              ) : (
                filteredRecipients.map((user) => (
                  <label
                    key={user.id}
                    className="connections-item"
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(user.id)}
                      onChange={() => toggleUser(user.id)}
                    />
                    <span className="connections-item__info">
                      <span className="connections-item__name">
                        {user.name || user.email}
                      </span>
                      <div className="connections-item__meta">{user.email}</div>
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}

        <div className="integrations-checkbox">
          <input
            type="checkbox"
            checked={channels.includes("telegram")}
            onChange={() => toggleChannel("telegram")}
          />
          Telegram
        </div>
        <div className="integrations-checkbox">
          <input
            type="checkbox"
            checked={channels.includes("email")}
            onChange={() => toggleChannel("email")}
          />
          Email
        </div>

        {error ? <p className="integrations-error">{error}</p> : null}

        <div className="integrations-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={broadcastMutation.isPending}
          >
            {broadcastMutation.isPending ? "Sending..." : "Send broadcast"}
          </Button>
        </div>
      </form>

      {lastResult ? (
        <div
          className="integrations-summary"
          style={{ marginTop: "16px", whiteSpace: "pre-wrap" }}
        >
          Sent to {lastResult.sent} of {lastResult.total} users (
          {lastResult.skipped} skipped, {lastResult.failed} failed).
        </div>
      ) : null}
    </Card>
  );
}
